/**
 * Excel Service - Extract text from Excel files using SheetJS
 *
 * Uses SheetJS (xlsx) library for client-side Excel parsing.
 * Supports .xlsx, .xls, .csv files.
 */

import * as XLSX from 'xlsx';

/**
 * Extract text content from an Excel file
 * @param {File} file - The Excel file to process
 * @returns {Promise<{text: string, sheetCount: number, sheets: Array, metadata: Object}>}
 */
export async function extractTextFromExcel(file) {
  try {
    // Read file as ArrayBuffer
    const arrayBuffer = await file.arrayBuffer();

    // Parse the workbook
    const workbook = XLSX.read(arrayBuffer, { type: 'array' });

    const sheetCount = workbook.SheetNames.length;
    const sheets = [];
    const textParts = [];

    // Extract data from each sheet
    for (const sheetName of workbook.SheetNames) {
      const worksheet = workbook.Sheets[sheetName];

      // Convert to JSON for structured data
      const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: '' });

      // Convert to text format
      const sheetText = jsonData
        .filter(row => row.some(cell => cell !== ''))
        .map(row => row.join(' | '))
        .join('\n');

      if (sheetText.trim()) {
        sheets.push({
          name: sheetName,
          rowCount: jsonData.length,
          text: sheetText,
        });

        textParts.push(`[Sheet: ${sheetName}]\n${sheetText}`);
      }
    }

    const fullText = textParts.join('\n\n');

    // Extract metadata
    const metadata = {
      sheetNames: workbook.SheetNames,
      creator: workbook.Props?.Creator || null,
      lastModifiedBy: workbook.Props?.LastModifiedBy || null,
      created: workbook.Props?.CreatedDate || null,
    };

    return {
      text: fullText,
      sheetCount,
      sheets,
      metadata,
      fileName: file.name,
      fileSize: file.size,
    };
  } catch (error) {
    console.error('Excel extraction error:', error);
    throw new Error(`Failed to extract text from Excel: ${error.message}`);
  }
}

/**
 * Check if a file is an Excel file
 * @param {File} file - The file to check
 * @returns {boolean}
 */
export function isExcel(file) {
  const excelTypes = [
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // xlsx
    'application/vnd.ms-excel', // xls
    'text/csv', // csv
  ];

  const excelExtensions = ['.xlsx', '.xls', '.csv'];
  const fileName = file.name.toLowerCase();

  return excelTypes.includes(file.type) || excelExtensions.some(ext => fileName.endsWith(ext));
}

/**
 * Get a preview summary of the Excel data
 * @param {string} text - The extracted text
 * @param {number} maxLength - Maximum preview length
 * @returns {string}
 */
export function getPreviewText(text, maxLength = 500) {
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength).trim() + '...';
}

export default {
  extractTextFromExcel,
  isExcel,
  getPreviewText,
};
